/**
 * 画面（ビューポート）の高さを測るフック。
 *
 * 全画面表示の図の器の高さを決めるために要る。全画面の覆いは画面をそのまま覆うので、器の高さも
 * 画面の高さから決まる。`flow-pane-width.ts` が測るのは**器の外枠の幅**であって画面ではない。
 * 同じフックに両方を持たせると、器を測って器の高さを決める形になり、決めた高さが次の観測を
 * 変える循環になる。測る対象が違うので別のフックに分ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の高さを測り、画面の大きさが変わるたびに測り直す。
 * @graph-connects none
 */
import { useEffect, useState } from 'react';

/**
 * 画面の高さを測り続ける。
 *
 * 初回の値は `useState` の初期化子でその場で読む。効果（`useEffect`）が走ってから読む形にすると、
 * 最初の描画だけ 0 になり、全画面へ切り替えた瞬間に器が潰れてから元に戻る形で 1 度ちらつく。
 *
 * 画面の大きさが変わったら測り直す。ウィンドウの大きさを変えても器の幅は変わらないことがある
 * （全画面の覆いは幅を画面いっぱいに取るので横の変化は幅の観測が拾うが、縦の変化は拾わない）ため、
 * 幅の観測に相乗りできない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の高さを測り、画面の大きさが変わるたびに測り直す。
 * @graph-connects none
 */
export function useViewportHeight(): number {
  const [height, setHeight] = useState(() => window.innerHeight);

  useEffect(() => {
    const measure = () => {
      setHeight(window.innerHeight);
    };
    /* 初期化子を読んでから効果が走るまでに大きさが変わっている場合があるので、ここでも測る。 */
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  return height;
}
